import { Divider, Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import React, { Activity, memo, ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { Extrapolation, interpolate, useAnimatedStyle } from 'react-native-reanimated';

import { ActionButton, type PlayerListTemplateV2ActionButtonProps } from './action-button';
import { usePlayerListTemplate } from './context';
import { findNonSlotChildren, findSlot, markSlot } from './slot-marker';

const HEADER_HEIGHT_FALLBACK = 64;

const CONTAINER_LAYER_STYLE = {
  position: 'relative',
  zIndex: 10,
  elevation: 10,
} as const;

export type PlayerListTemplateV2ToolbarProps = {
  /** Tablet-only title (left of the pagination row). */
  titleResultsSummary?: string;
  /** Toggles both the tablet title & the "Showing N of M results" subtitle. */
  isResultsSummaryVisible?: boolean;
  children?: ReactNode;
};

/**
 * Orchestrates the search/filter/pagination/action-button row under the
 * screen header. Discovers its children via slot markers so users can compose
 * freely without worrying about layout order — the toolbar applies the correct
 * phone vs tablet arrangement automatically.
 */
function ToolbarComponent({
  titleResultsSummary,
  isResultsSummaryVisible = true,
  children,
}: PlayerListTemplateV2ToolbarProps) {
  const { isTablet } = useScaling();
  const searchSlot = findSlot(children, 'search');
  const filterSlot = findSlot(children, 'filter');
  const paginationSlot = findSlot(children, 'pagination');
  const actionSlot = findSlot(children, 'action') as
    | ReactElement<PlayerListTemplateV2ActionButtonProps>
    | undefined;
  const extraChildren = findNonSlotChildren(children);

  const actionContent = useMemo<ReactNode>(() => {
    if (!actionSlot || actionSlot.type !== ActionButton) return null;
    const { mobile, tablet, children: slotChildren } = actionSlot.props;
    const deviceSpecific = isTablet ? tablet : mobile;
    return deviceSpecific ?? slotChildren ?? null;
  }, [actionSlot, isTablet]);

  if (isTablet) {
    return (
      <ToolbarTablet
        filterSlot={filterSlot}
        searchSlot={searchSlot}
        paginationSlot={paginationSlot}
        actionContent={actionContent}
        extraChildren={extraChildren}
        titleResultsSummary={titleResultsSummary}
        isResultsSummaryVisible={isResultsSummaryVisible}
      />
    );
  }

  return (
    <ToolbarMobile
      filterSlot={filterSlot}
      searchSlot={searchSlot}
      paginationSlot={paginationSlot}
      actionContent={actionContent}
      extraChildren={extraChildren}
      isResultsSummaryVisible={isResultsSummaryVisible}
    />
  );
}

type ToolbarVariantProps = {
  filterSlot: ReactElement | undefined;
  searchSlot: ReactElement | undefined;
  paginationSlot: ReactElement | undefined;
  actionContent: ReactNode;
  extraChildren: ReactNode[];
  isResultsSummaryVisible: boolean;
};

function ToolbarMobile({
  filterSlot,
  searchSlot,
  paginationSlot,
  actionContent,
  extraChildren,
  isResultsSummaryVisible,
}: ToolbarVariantProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const {
    scrollY,
    buttonCreateProfileHeight,
    setHeaderActionMeasuredHeight,
    setHasActionButton,
    listLogic,
  } = usePlayerListTemplate();
  const resultSummary = useResultSummary(isResultsSummaryVisible);

  const hasActionButton = actionContent != null;
  const headerHeightFallback = hasActionButton ? scale(HEADER_HEIGHT_FALLBACK) : 0;
  const actionButtonTopOffset = scale(20);
  const paginationVisible = (listLogic.paginatedMeta?.totalPages ?? 0) > 1;

  useEffect(() => {
    setHasActionButton(hasActionButton);
  }, [hasActionButton, setHasActionButton]);

  const filterContainerAnimatedStyle = useAnimatedStyle(() => {
    const h =
      buttonCreateProfileHeight.value > 0 ? buttonCreateProfileHeight.value : headerHeightFallback;
    const filterTranslateY = interpolate(scrollY.value, [0, h], [h, 0], {
      extrapolateLeft: Extrapolation.CLAMP,
      extrapolateRight: Extrapolation.CLAMP,
    });
    return { transform: [{ translateY: filterTranslateY }], backgroundColor: colors['2'] };
  });

  const actionButtonAnimatedStyle = useAnimatedStyle(() => {
    const h =
      buttonCreateProfileHeight.value > 0 ? buttonCreateProfileHeight.value : headerHeightFallback;
    const travel = h + actionButtonTopOffset;
    const buttonTranslateY = interpolate(scrollY.value, [0, travel], [0, -travel], {
      extrapolateLeft: Extrapolation.CLAMP,
      extrapolateRight: Extrapolation.CLAMP,
    });
    return { transform: [{ translateY: buttonTranslateY }] };
  });

  return (
    <View collapsable={false} style={CONTAINER_LAYER_STYLE}>
      <Stack
        paddingTop={scale(20)}
        paddingBottom={scale(14)}
        paddingX={scale({ phone: 0, tablet: 16 })}
        zIndex={999}>
        <Activity mode={hasActionButton ? 'visible' : 'hidden'}>
          <Animated.View
            style={[
              styles.actionButtonContainer,
              { top: actionButtonTopOffset },
              actionButtonAnimatedStyle,
            ]}
            onLayout={e => {
              const h = e.nativeEvent.layout.height;
              buttonCreateProfileHeight.value = h;
              setHeaderActionMeasuredHeight(h);
            }}>
            {actionContent}
          </Animated.View>
        </Activity>

        <Animated.View style={filterContainerAnimatedStyle}>
          <Stack width="100%" gap={scale(8)} paddingX={scale({ phone: 18, tablet: 0 })}>
            {searchSlot}
            {filterSlot}
            <Activity mode={resultSummary ? 'visible' : 'hidden'}>
              <Text
                fontFamily="Montserrat"
                fontSize={scale(8)}
                fontWeight="400"
                lineHeight={scale(10)}
                style={styles.resultsSummary}>
                {resultSummary}
              </Text>
            </Activity>
          </Stack>

          {paginationVisible && paginationSlot ? (
            <Stack
              width="100%"
              align="center"
              paddingX={scale({ phone: 18, tablet: 0 })}
              paddingBottom={scale(8)}
              paddingTop={scale(12)}>
              {paginationSlot}
            </Stack>
          ) : (
            <Stack height={scale(4)} />
          )}
        </Animated.View>
        {extraChildren}
      </Stack>
    </View>
  );
}

type ToolbarTabletProps = ToolbarVariantProps & {
  titleResultsSummary?: string;
};

function ToolbarTablet({
  filterSlot,
  searchSlot,
  paginationSlot,
  actionContent,
  extraChildren,
  titleResultsSummary,
  isResultsSummaryVisible,
}: ToolbarTabletProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const hasExtra = extraChildren.length > 0;
  const resultSummary = useResultSummary(isResultsSummaryVisible);

  return (
    <View collapsable={false} style={CONTAINER_LAYER_STYLE}>
      <Stack
        paddingTop={scale(20)}
        paddingBottom={scale(14)}
        paddingX={scale({ phone: 0, tablet: 16 })}>
        <Stack
          direction="row"
          align="center"
          width="100%"
          gap={scale(12)}
          paddingX={scale({ phone: 18, tablet: 0 })}>
          <Stack
            direction="row"
            align="center"
            gap={scale(12)}
            style={styles.tabletActionsContainer}>
            {filterSlot}
          </Stack>

          <Stack
            flexShrink={0}
            flex={hasExtra ? 1 : undefined}
            width={hasExtra ? undefined : scale(360)}>
            {searchSlot}
          </Stack>

          {!hasExtra ? <Stack flex={1} /> : null}

          {actionContent}
          {extraChildren}
        </Stack>

        <Divider thickness={StyleSheet.hairlineWidth} color={colors['6']} spacing={scale(12)} />

        <Stack
          direction="row"
          width="100%"
          paddingX={scale({ phone: 18, tablet: 0 })}
          justify="space-between"
          align="center">
          <Activity mode={isResultsSummaryVisible ? 'visible' : 'hidden'}>
            <Text
              variant="Font Inter/Subtitle/Bold 14px"
              transform="uppercase"
              style={[styles.tabletTitle, { minWidth: scale(200) }]}>
              {titleResultsSummary}
            </Text>
          </Activity>
          <Stack flex={1} align="center">
            {paginationSlot}
          </Stack>
          <Activity mode={resultSummary ? 'visible' : 'hidden'}>
            <Text
              variant="Font Inter/Subtitle/Regular 11px"
              style={[styles.resultsSummary, styles.tabletResultsSummary, styles.summaryText]}>
              {resultSummary}
            </Text>
          </Activity>
        </Stack>
      </Stack>
    </View>
  );
}

function useResultSummary(isResultsSummaryVisible: boolean): string {
  const { listLogic } = usePlayerListTemplate();
  return useMemo(() => {
    if (!isResultsSummaryVisible) return '';
    const meta = listLogic.paginatedMeta;
    const totalResult = meta?.total ?? 0;
    const limit = meta?.limit ?? 0;
    const currentPage = meta?.page ?? 1;
    if (totalResult <= 0 || limit <= 0) return '';

    const startIndex = Math.max(0, currentPage - 1) * limit + 1;
    const endIndex = Math.min(currentPage * limit, totalResult);
    const lastActive = listLogic.lastActive;

    if (lastActive === 'show_all') {
      return STRINGS.player.listPlayers.resultsSummaryShowAll(startIndex, endIndex, totalResult);
    }
    const lastActiveLabel = getLastActiveLabel(lastActive);
    return STRINGS.player.listPlayers.resultsSummaryWithLastActive(
      startIndex,
      endIndex,
      totalResult,
      lastActiveLabel,
    );
  }, [isResultsSummaryVisible, listLogic.paginatedMeta, listLogic.lastActive]);
}

function getLastActiveLabel(lastActive: string): string {
  switch (lastActive) {
    case '1_month':
      return STRINGS.player.filters.lastActive.oneMonth;
    case '3_months':
      return STRINGS.player.filters.lastActive.threeMonths;
    case '6_months':
      return STRINGS.player.filters.lastActive.sixMonths;
    case '1_5_years':
      return STRINGS.player.filters.lastActive.oneHalfYears;
    default:
      return STRINGS.player.filters.lastActive.showAll;
  }
}

const MemoToolbar = memo(ToolbarComponent);
MemoToolbar.displayName = 'PlayerListTemplateV2.Toolbar';

export const Toolbar = markSlot(MemoToolbar, 'toolbar');

const useStyles = createStyles(({ scale }) => ({
  actionButtonContainer: {
    zIndex: 2,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  resultsSummary: {
    color: colors['6'],
    textAlign: 'center',
  },
  tabletResultsSummary: {
    textAlign: 'left',
  },
  tabletActionsContainer: {
    flexShrink: 0,
  },
  tabletTitle: {
    color: colors.Blue,
  },
  summaryText: { minWidth: scale(200), textAlign: 'right' },
}));
